import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  TextField,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import apiService from '../../services/api';

/**
 * Export Dialog Component
 *
 * Allows users to export transcripts in various formats with configurable options.
 */
const ExportDialog = ({ open, onClose, transcriptId, segments, audioFilename }) => {
  // Export options state
  const [format, setFormat] = useState('plain');
  const [includeSpeakers, setIncludeSpeakers] = useState(true);
  const [includeTimestamps, setIncludeTimestamps] = useState(false);
  const [timestampInterval, setTimestampInterval] = useState('');

  // UI state
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Generate preview whenever options change
  useEffect(() => {
    if (open && segments && segments.length > 0) {
      generatePreview();
    }
  }, [format, includeSpeakers, includeTimestamps, timestampInterval, open, segments]);

  /**
   * Generate a preview of the export based on current options.
   * Takes first 3 segments for preview.
   */
  const generatePreview = () => {
    if (!segments || segments.length === 0) {
      setPreview('No segments available for preview.');
      return;
    }

    const previewSegments = segments.slice(0, 3);
    let previewText = '';

    if (format === 'plain') {
      previewSegments.forEach(segment => {
        const text = segment.text || '';
        if (includeSpeakers && segment.speaker) {
          previewText += `${segment.speaker}: ${text}\n`;
        } else {
          previewText += `${text}\n`;
        }
      });
    } else if (format === 'timestamped') {
      previewSegments.forEach(segment => {
        const text = segment.text || '';
        const start = segment.start || 0;
        const timestamp = formatTimestamp(start);

        let line = `[${timestamp}]`;
        if (includeSpeakers && segment.speaker) {
          line += ` ${segment.speaker}:`;
        }
        line += ` ${text}\n`;
        previewText += line;
      });
    } else if (format === 'srt') {
      previewSegments.forEach((segment, index) => {
        const text = segment.text || '';
        const start = segment.start || 0;
        const end = segment.end || start + 1;
        const startTs = formatTimestampSrt(start);
        const endTs = formatTimestampSrt(end);

        let subtitleText = text;
        if (includeSpeakers && segment.speaker) {
          subtitleText = `${segment.speaker}: ${text}`;
        }

        previewText += `${index + 1}\n${startTs} --> ${endTs}\n${subtitleText}\n\n`;
      });
    }

    if (segments.length > 3) {
      previewText += '\n... (preview showing first 3 segments)';
    }

    setPreview(previewText);
  };

  /**
   * Format timestamp for readable display (HH:MM:SS).
   */
  const formatTimestamp = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  /**
   * Format timestamp for SRT format (HH:MM:SS,mmm).
   */
  const formatTimestampSrt = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const millis = Math.floor((seconds % 1) * 1000);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')},${String(millis).padStart(3, '0')}`;
  };

  /**
   * Handle export button click.
   * Calls API and triggers file download.
   */
  const handleExport = async () => {
    setExporting(true);
    setError(null);

    try {
      const exportOptions = {
        format: format,
        include_speakers: includeSpeakers,
        include_timestamps: format === 'timestamped' ? true : includeTimestamps,
        timestamp_interval: timestampInterval ? parseInt(timestampInterval) : null,
      };

      const response = await apiService.exportTranscription(transcriptId, exportOptions);

      // Create download link
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `transcript_${format}.txt`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Close dialog on success
      onClose();
    } catch (err) {
      console.error('Export error:', err);
      setError(err.response?.data?.detail || 'Failed to export transcript. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  /**
   * Handle format change.
   */
  const handleFormatChange = (event) => {
    const newFormat = event.target.value;
    setFormat(newFormat);

    // Auto-enable timestamps for timestamped format
    if (newFormat === 'timestamped') {
      setIncludeTimestamps(true);
    }
  };

  /**
   * Reset state when dialog closes.
   */
  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Export Transcript</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Export Format</FormLabel>
            <RadioGroup value={format} onChange={handleFormatChange}>
              <FormControlLabel
                value="plain"
                control={<Radio />}
                label="Plain Text - Simple text with optional speaker names"
              />
              <FormControlLabel
                value="timestamped"
                control={<Radio />}
                label="Timestamped Text - Text with timestamps at intervals"
              />
              <FormControlLabel
                value="srt"
                control={<Radio />}
                label="SRT Subtitle Format - Standard subtitle file format"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Export Options
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeSpeakers}
                onChange={(e) => setIncludeSpeakers(e.target.checked)}
              />
            }
            label="Include speaker names"
          />
          {format === 'timestamped' && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeTimestamps}
                    onChange={(e) => setIncludeTimestamps(e.target.checked)}
                    disabled
                  />
                }
                label="Include timestamps (required for timestamped format)"
              />
              <TextField
                label="Timestamp Interval (seconds)"
                type="number"
                value={timestampInterval}
                onChange={(e) => setTimestampInterval(e.target.value)}
                placeholder="Leave empty for every segment"
                size="small"
                sx={{ ml: 4, mt: 1 }}
                helperText="Optional: Show timestamps at regular intervals"
              />
            </>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Preview
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              backgroundColor: '#f5f5f5',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            {preview || 'No preview available'}
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
          disabled={exporting || !segments || segments.length === 0}
        >
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDialog;
